/**
 * <RegionPicker /> — typographic dropdown for region selection.
 *
 * Reads + writes via `useRegion()`. NO flag emojis, hairline borders, mono
 * font on the trigger label. Closes on outside click or Escape.
 *
 * Props:
 *  - placement: "nav" | "menu" | "field"  — adjusts trigger styling lightly.
 *  - onChange?: (region) => void           — optional override; defaults to setRegion.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { useRegion, VALID_REGIONS, DISPLAY_REGIONS } from "@/contexts/RegionContext";

const PLACEMENT_STYLES = {
    nav: {
        trigger:
            "inline-flex items-center gap-1.5 px-2.5 py-1 tc-mono text-[11px] text-[#e6ebec] border border-[#1a2124] hover:border-[#01696f] transition-colors duration-200",
        panel:
            "bg-[#0e1416] border border-[#1a2124] text-[#e6ebec] shadow-xl",
        option:
            "text-[#e6ebec] hover:bg-[#1a2124]",
        optionMuted:
            "text-[#8a9498]",
    },
    menu: {
        trigger:
            "w-full inline-flex items-center justify-between gap-2 px-4 py-2.5 text-[12px] text-[#e6ebec] hover:bg-[#1a2124] transition-colors",
        panel:
            "bg-[#0e1416] border border-[#1a2124] text-[#e6ebec] shadow-xl",
        option:
            "text-[#e6ebec] hover:bg-[#1a2124]",
        optionMuted:
            "text-[#8a9498]",
    },
    field: {
        trigger:
            "w-full inline-flex items-center justify-between gap-2 px-3.5 py-2 tc-mono text-[12.5px] text-[#0e1416] bg-white border border-[#d8dcdd] hover:border-[#01696f] transition-colors",
        panel:
            "bg-white border border-[#d8dcdd] text-[#0e1416] shadow-md",
        option:
            "text-[#0e1416] hover:bg-[#f6f7f6]",
        optionMuted:
            "text-[#5a6669]",
    },
};

const PANEL_MIN_W = "min-w-[14rem]";

export const RegionPicker = ({ placement = "nav", onChange }) => {
    const { region, setRegion, catalogue } = useRegion();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const wrapperRef = useRef(null);
    const styles = PLACEMENT_STYLES[placement] || PLACEMENT_STYLES.nav;

    const current = catalogue[region] || {};

    // Outside-click and Escape handlers
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (!wrapperRef.current) return;
            if (!wrapperRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const handleSelect = useCallback(async (opt) => {
        if (opt.pipeline) {
            toast.info(`${opt.label} — coming soon. Contact us for pipeline access.`);
            return;
        }
        const nextBackend = opt.backend;
        if (nextBackend === region) {
            setOpen(false);
            return;
        }
        setBusy(true);
        try {
            if (onChange) {
                await onChange(nextBackend);
            } else {
                await setRegion(nextBackend);
            }
            setOpen(false);
        } catch {
            /* hook surfaces a toast on failure; keep panel open so the user can retry */
        } finally {
            setBusy(false);
        }
    }, [onChange, region, setRegion]);

    const options = useMemo(
        () =>
            DISPLAY_REGIONS.map((r) => {
                const backendEntry = r.backend ? (catalogue[r.backend] || {}) : {};
                return {
                    code: r.code,
                    backend: r.backend,
                    pipeline: r.pipeline,
                    shortLabel: r.short_label,
                    label: backendEntry.label || r.label,
                    currency: backendEntry.currency || "USD",
                    symbol: backendEntry.currency_symbol || "$",
                };
            }),
        [catalogue],
    );

    return (
        <div
            ref={wrapperRef}
            className="relative"
            data-testid={`region-picker-${placement}`}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={`Region — current ${current.label || region}`}
                data-testid="region-picker-trigger"
                disabled={busy}
                className={styles.trigger}
            >
                {placement === "menu" && (
                    <span className="tc-mono text-[10px] text-[#5a6669] uppercase tracking-wider">
                        Region
                    </span>
                )}
                <span className="inline-flex items-center gap-1.5">
                    <span className="tc-mono font-medium">
                        {(DISPLAY_REGIONS.find((r) => r.backend === region) || {}).short_label || current.short_label || region}
                    </span>
                    {busy ? (
                        <Loader2
                            size={12}
                            strokeWidth={1.5}
                            className="animate-spin opacity-70"
                        />
                    ) : (
                        <ChevronDown
                            size={12}
                            strokeWidth={1.5}
                            className={`opacity-70 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
                        />
                    )}
                </span>
            </button>

            {open && (
                <div
                    role="listbox"
                    data-testid="region-picker-panel"
                    className={`absolute right-0 top-full mt-1 z-50 ${PANEL_MIN_W} ${styles.panel} transition-opacity duration-100`}
                >
                    {options.map((opt) => {
                        const selected = !opt.pipeline && opt.backend === region;
                        return (
                            <button
                                key={opt.code}
                                type="button"
                                role="option"
                                aria-selected={selected}
                                data-testid={`region-picker-option-${opt.code}`}
                                onClick={() => handleSelect(opt)}
                                disabled={busy}
                                title={opt.pipeline ? "Coming soon — contact us for pipeline access" : ""}
                                className={`relative w-full text-left px-4 py-2.5 transition-colors ${styles.option} ${opt.pipeline ? "opacity-70 cursor-help" : ""}`}
                            >
                                {selected && (
                                    <span
                                        aria-hidden="true"
                                        className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#01696f]"
                                    />
                                )}
                                <div className="tc-mono text-[12px] font-medium flex items-center gap-2">
                                    {opt.shortLabel}
                                    {opt.pipeline && (
                                        <span
                                            data-testid={`region-picker-pipeline-${opt.code}`}
                                            className="px-1.5 py-[1px] tc-mono text-[9px] tracking-wider uppercase border border-[#b3741f] text-[#b3741f]"
                                        >
                                            pipeline
                                        </span>
                                    )}
                                </div>
                                <div className={`mt-0.5 tc-mono text-[10px] ${styles.optionMuted}`}>
                                    {opt.label}{opt.pipeline ? "" : ` · USD $`}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default RegionPicker;
